/**
 * Drawer component with Dashboard3 glass morphism styling
 *
 * @example
 * ```tsx
 * <Drawer isOpen={isOpen} onClose={onClose} title="Menu">
 *   <DrawerBody>Content here</DrawerBody>
 * </Drawer>
 * ```
 */

"use client";

import {
  Drawer as ChakraDrawer,
  type DrawerProps as ChakraDrawerProps,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  type DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
} from "@chakra-ui/react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";
import { getPortaledSurfaceProps } from "@/lib/theme/portaled-surface";

export interface DrawerProps extends ChakraDrawerProps {
  /**
   * Drawer title
   */
  title?: string;
  /**
   * Props merged onto DrawerContent (`data-testid` for modal surface, etc.).
   */
  contentProps?: DrawerContentProps & { [key: `data-${string}`]: string };
}

export function Drawer({
  title,
  children,
  contentProps,
  ...props
}: DrawerProps) {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const surfaceProps = getPortaledSurfaceProps(theme, interactive, "glass");

  return (
    <ChakraDrawer {...props}>
      <DrawerOverlay
        bg={interactive.overlay.drawer}
        backdropFilter={interactive.backdrop.blurOverlay}
      />
      <DrawerContent
        {...contentProps}
        borderRightWidth="1px"
        borderColor={theme.borderGlass.default}
        {...surfaceProps}
      >
        {title && (
          <DrawerHeader
            borderBottomWidth="1px"
            borderBottomColor={theme.borderGlass.default}
          >
            {title}
          </DrawerHeader>
        )}
        <DrawerCloseButton />
        {children}
      </DrawerContent>
    </ChakraDrawer>
  );
}

export {
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
};
